import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Paper,
} from '@mui/material';
import { questions } from '../../data/questions';
import { AnswerData } from '../../types';

interface Props {
  onSubmit: (answers: AnswerData[]) => void;
  onBack: () => void;
}

const PortableSkillsTest: React.FC<Props> = ({ onSubmit, onBack }) => {
  const testQuestions = questions.filter((q) => q.category === 'portable-skills');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: string]: number }>({});
  const questionRef = useRef<HTMLDivElement>(null);

  if (testQuestions.length === 0) {
    return (
      <Box>
        <Typography variant="h5" color="error" gutterBottom>
          エラー: 質問データが見つかりません
        </Typography>
        <Button onClick={onBack} variant="outlined">
          戻る
        </Button>
      </Box>
    );
  }

  const currentQuestion = testQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / testQuestions.length) * 100;

  const handleAnswer = (value: number) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }));

    // 最後の質問以外は自動で次へ進む
    if (currentQuestionIndex < testQuestions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex((prev) => prev + 1);
        questionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < testQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      questionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      const answerData: AnswerData[] = Object.entries(answers).map(
        ([questionId, score]) => ({
          questionId,
          score,
        })
      );
      onSubmit(answerData);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      questionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      onBack();
    }
  };

  const isAnswered = answers[currentQuestion.id] !== undefined;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
        ポータブルスキル診断
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
        どの職業でも活かせるあなたのスキルを診断します。これが最後のテストです。全{testQuestions.length}
        問に回答してください。
      </Typography>

      {/* プログレスバー */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            進捗状況
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentQuestionIndex + 1} / {testQuestions.length}
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
      </Box>

      {/* 質問カード */}
      <Paper
        ref={questionRef}
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          border: '2px solid #e0e0e0',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
          【{currentQuestion.subCategory}】
        </Typography>
        <Typography variant="h6" sx={{ mb: 4, lineHeight: 1.8 }}>
          {currentQuestion.text}
        </Typography>

        <RadioGroup
          value={answers[currentQuestion.id] || ''}
          onChange={(e) => handleAnswer(parseInt(e.target.value))}
        >
          <FormControlLabel value={5} control={<Radio />} label="非常に得意" sx={{ mb: 1.5 }} />
          <FormControlLabel value={4} control={<Radio />} label="やや得意" sx={{ mb: 1.5 }} />
          <FormControlLabel value={3} control={<Radio />} label="どちらともいえない" sx={{ mb: 1.5 }} />
          <FormControlLabel value={2} control={<Radio />} label="やや苦手" sx={{ mb: 1.5 }} />
          <FormControlLabel value={1} control={<Radio />} label="非常に苦手" />
        </RadioGroup>
      </Paper>

      {/* ナビゲーションボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" onClick={handlePrevious} sx={{ px: 4 }}>
          前の質問
        </Button>
        <Button variant="contained" onClick={handleNext} disabled={!isAnswered} sx={{ px: 4 }}>
          {currentQuestionIndex === testQuestions.length - 1 ? '診断結果を見る' : '次の質問'}
        </Button>
      </Box>
    </Box>
  );
};

export default PortableSkillsTest;
